import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet } from "../lib/api.js";
import { FormRenderer, PREVIEW_WIDTHS, type PreviewWidth } from "@sga/shared/form-renderer";
import { SEPLAT_BRAND_COLORS, SEPLAT_BRAND_FONTS } from "@sga/shared";
import type { DraftField, DraftSection, LogicRule } from "@sga/shared";
import type { FieldDefinition } from "@sga/shared";

/**
 * Live preview at desktop / tablet / mobile widths, rendered with the *same* components
 * `apps/public` uses (packages/shared/src/form-renderer) — so what a coordinator previews is
 * literally what an applicant sees, rather than a lookalike that drifts.
 */
export const PreviewPane = ({
  fields,
  sections,
  logic,
  width,
  onWidthChange,
}: {
  fields: DraftField[];
  sections: DraftSection[];
  logic: LogicRule[];
  /**
   * Controlled from FormBuilder so the pane and the column it sits in agree on the simulated
   * width. The column itself stays a normal, screen-safe size — widening it to the device width
   * pushed the builder's total width past the viewport and the panes overlapped.
   */
  width: PreviewWidth;
  onWidthChange: (width: PreviewWidth) => void;
}): JSX.Element => {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [page, setPage] = useState(1);
  const lookups = useNgLookups(fields.some((field) => field.type === "ng_state_lga"));

  /**
   * The frame renders at the device's real width and is then scaled down to whatever room the
   * column has, the way a browser's own device-emulation view does it.
   *
   * The alternative — sizing the frame to the space available — is what made every width look the
   * same: a 1024px "desktop" squeezed into a 420px column is a 420px layout, so the toggle changed
   * nothing an applicant would ever see. Rendering at 1024 and scaling keeps the layout decisions
   * (which fields share a row, where text wraps) faithful, and only the pixels get smaller.
   */
  const viewportRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [available, setAvailable] = useState(0);
  const [naturalHeight, setNaturalHeight] = useState(0);

  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setAvailable(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Layout effect, not a plain one: the placeholder below is sized from this height, and measuring
  // after paint would show a frame overlapping whatever follows it for one frame on every edit.
  useLayoutEffect(() => {
    const node = frameRef.current;
    if (!node) return;
    const observer = new ResizeObserver(() => setNaturalHeight(node.offsetHeight));
    observer.observe(node);
    setNaturalHeight(node.offsetHeight);
    return () => observer.disconnect();
  }, []);

  const targetWidth = PREVIEW_WIDTHS[width];
  // Never scale up: a 390px mobile frame in a wide column should stay 390px, not stretch.
  const scale = available > 0 ? Math.min(1, available / targetWidth) : 1;

  const pages = [...new Set(sections.map((section) => section.pageNumber))].sort((a, b) => a - b);
  const pagesToShow = pages.length > 0 ? pages : [1];

  const branding = {
    ...SEPLAT_BRAND_COLORS,
    headingFont: SEPLAT_BRAND_FONTS.stack,
    bodyFont: SEPLAT_BRAND_FONTS.stack,
  };

  const rendererFields: FieldDefinition[] = fields.map((field) => ({
    key: field.key,
    type: field.type,
    label: field.label,
    isRequired: field.isRequired,
    visibility: field.visibility,
    config: { ...field.config, helpText: field.helpText },
    sectionKey: field.sectionKey ?? null,
    // Carried through so the renderer can group side-by-side rows and order fields correctly.
    ...({
      position: field.position,
      rowGroup: field.rowGroup,
      widthSpan: field.widthSpan,
    } as Record<string, number>),
  }));

  return (
    <div className="flex h-full flex-col bg-secondary/40">
      <div className="flex items-center justify-between gap-2 border-b bg-card px-3 py-2">
        <div className="flex gap-1" role="group" aria-label="Preview width">
          {(Object.keys(PREVIEW_WIDTHS) as PreviewWidth[]).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onWidthChange(option)}
              aria-pressed={width === option}
              className={`rounded px-2 py-1 text-xs capitalize ${
                width === option ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
              }`}
            >
              {option}
            </button>
          ))}
          {/* Say the zoom out loud, so a coordinator reads small text as "scaled down" rather than
              as how it will look on the applicant's phone. */}
          <span className="self-center pl-1 text-[11px] tabular-nums text-muted-foreground">
            {targetWidth}px{scale < 0.995 && ` · ${Math.round(scale * 100)}%`}
          </span>
        </div>

        {pagesToShow.length > 1 && (
          <div className="flex items-center gap-1 text-xs">
            <button
              type="button"
              className="rounded border px-2 py-1 disabled:opacity-40"
              disabled={page <= Math.min(...pagesToShow)}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </button>
            <span className="px-1 text-muted-foreground">
              Step {pagesToShow.indexOf(page) + 1} of {pagesToShow.length}
            </span>
            <button
              type="button"
              className="rounded border px-2 py-1 disabled:opacity-40"
              disabled={page >= Math.max(...pagesToShow)}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      <div ref={viewportRef} className="flex-1 overflow-y-auto p-4">
        {/* Placeholder carrying the *scaled* footprint. A CSS transform does not affect layout, so
            without this the column would still reserve the frame's full unscaled size and overlap
            everything below it. */}
        <div
          className="mx-auto"
          style={{ width: targetWidth * scale, height: naturalHeight * scale }}
        >
          <div
            ref={frameRef}
            className="rounded-lg border bg-white p-4 shadow-sm"
            style={{
              width: targetWidth,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
          >
            {fields.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Add a field to see the preview.
              </p>
            ) : (
              <FormRenderer
                fields={rendererFields}
                sections={sections.map((section) => ({
                  key: section.key,
                  title: section.title ?? null,
                  description: section.description ?? null,
                  pageNumber: section.pageNumber,
                  position: section.position,
                }))}
                logic={logic}
                values={values}
                onChange={(key, value) => {
                  setValues((current) => ({ ...current, [key]: value }));
                  // A state selection has to fetch that state's LGAs.
                  const field = fields.find((entry) => entry.key === key);
                  if (field?.type === "ng_state_lga") {
                    const state = (value as { state?: string })?.state;
                    if (state) lookups.onStateSelected(state);
                  }
                }}
                branding={branding}
                pageNumber={pagesToShow.length > 1 ? page : undefined}
                lookups={{ states: lookups.states, lgasForState: lookups.lgasForState }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * The 36 states + FCT and their 774 LGAs, from the same public endpoints the applicant renderer
 * calls. Both are reference data behind a 24-hour cache header, so they are fetched once per
 * builder session and never invalidated.
 *
 * Previously the pane passed empty lists with a comment deferring this to "Phase 5's public
 * renderer" — which meant a `ng_state_lga` field previewed as two dropdowns with nothing in them,
 * indistinguishable from a broken question.
 */
const useNgLookups = (
  enabled: boolean,
): {
  states: { code: string; name: string }[];
  lgasForState: (state: string) => { id: string; name: string }[];
  onStateSelected: (state: string) => void;
} => {
  const queryClient = useQueryClient();
  const [lgasByState, setLgasByState] = useState<Record<string, { id: string; name: string }[]>>(
    {},
  );

  const statesQuery = useQuery({
    queryKey: ["ng-states"],
    queryFn: () => apiGet<{ data: { code: string; name: string }[] }>("/public/lookup/states"),
    enabled,
    staleTime: Infinity,
  });

  const onStateSelected = useCallback(
    (state: string) => {
      if (lgasByState[state]) return;
      void queryClient
        .fetchQuery({
          queryKey: ["ng-lgas", state],
          queryFn: () =>
            apiGet<{ data: { id: string; name: string }[] }>(
              `/public/lookup/lgas?state=${encodeURIComponent(state)}`,
            ),
          staleTime: Infinity,
        })
        .then((result) => setLgasByState((current) => ({ ...current, [state]: result.data })))
        .catch(() => undefined);
    },
    [lgasByState, queryClient],
  );

  const lgasForState = useCallback((state: string) => lgasByState[state] ?? [], [lgasByState]);

  return useMemo(
    () => ({ states: statesQuery.data?.data ?? [], lgasForState, onStateSelected }),
    [statesQuery.data, lgasForState, onStateSelected],
  );
};
